"use client"
import { keepSelection } from "@/actions/selection/keep-selection"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import { useKeyPress } from "@/app/(site)/_utils/_hooks/useKeyPres"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { t } from "@/translations"

import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import placeholder from "@assets/images/placeholder.png"
import { useQuery } from "@tanstack/react-query"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React from "react"
import Text from "../../../../Atoms/Text"
import ButtonPill from "../../../../Molecules/ButtonPill"
import helperSizeGuide from "./helpers"

type SizeGuideModalProps = { product: ProductData }

export default function SizeGuideModal({ product }: SizeGuideModalProps) {
    const [isAnimating, setIsAnimating] = React.useState(false)
    const toggleLensGuideModal = useEcommerceStore(state => state.toggleSizeGuideMenu)
    const closeSizeGuideModal = useEcommerceStore(state => state.closeSizeGuideMenu)
    const isLensGuideModalOpen = useEcommerceStore(state => state.isSizeGuideMenuOpen)
    const modalRef = React.useRef<HTMLElement>(null)
    const pathname = usePathname()
    const langCode = getLangCode(pathname.split("/")[1]) || ""

    // Function to toggle body scroll
    const toggleBodyScroll = (isModalOpen: boolean) => {}
    useClickOutside({ refs: [modalRef], onOutsideClick: toggleLensGuideModal })
    useKeyPress({ targetKeys: ["Escape", "Backspace"], action: closeSizeGuideModal })

    const { data, error, isFetched, isLoading } = useQuery({
        queryKey: ["selection"],
        queryFn: () => keepSelection()
    })

    React.useEffect(() => {
        // Disable scroll when component mounts
        toggleBodyScroll(isLensGuideModalOpen)

        // Enable scroll when component unmounts
        return () => toggleBodyScroll(false)
    }, [isLensGuideModalOpen])

    if (!isLensGuideModalOpen) return null
    if (!data?.success) return null

    const { sizeGuideImageUrl } = helperSizeGuide(product, data)

    const blurClasses = isAnimating ? "opacity-100" : "opacity-100"

    return (
        <div>
            <div
                className={`fixed top-0 z-30 h-full w-[100%] bg-black/20 backdrop-blur-md transition-all duration-300 ease-in-out ${blurClasses}`}
            />

            <div
                ref={modalRef as React.RefObject<HTMLDivElement>}
                className="fixed bottom-0 left-0 right-0 top-0 z-40 flex h-full overflow-y-auto bg-main-white px-16 py-24 sm:left-auto sm:w-552 md:bottom-auto md:px-24 md:py-32"
            >
                <div className="flex w-full flex-col justify-between">
                    <div>
                        <div className="mb-16 flex items-center justify-between md:mb-32">
                            <Text size="medium" weight="regular" className="col-span-5">
                                {product?.size_guide_type?.title}
                            </Text>
                            <div
                                className="col-span-1 flex cursor-pointer justify-end"
                                onClick={() => toggleLensGuideModal()}
                            >
                                <IconClose />
                            </div>
                        </div>

                        <div>
                            <Image
                                src={sizeGuideImageUrl ?? placeholder}
                                alt={product.name}
                                width={0}
                                height={0}
                                sizes="100vw"
                                style={{ width: "100%", height: "auto" }}
                            />
                        </div>
                    </div>
                    <div>
                        <ButtonPill
                            color="black"
                            variant="primary"
                            size="large"
                            className="w-full"
                            aria-label="Close"
                            onClick={() => toggleLensGuideModal()}
                        >
                            {t(langCode, "common.close")}
                        </ButtonPill>
                    </div>
                </div>
            </div>
        </div>
    )
}
